import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../../components/feature/AdminLayout';
import {
  getRiders, addRider, removeRider, toggleRiderActive, Rider,
} from '../../../utils/riderStorage';
import { getAllOrders } from '../../../utils/orderStorage';
import { useToast } from '../../../contexts/ToastContext';

const AVATAR_COLORS = [
  'from-orange-400 to-amber-500',
  'from-green-400 to-emerald-500',
  'from-teal-400 to-cyan-500',
  'from-violet-400 to-purple-400',
  'from-pink-400 to-rose-400',
  'from-sky-400 to-blue-400',
];
const avatarColor = (name: string) =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

export default function AdminRidersPage() {
  const toast = useToast();
  const [riders, setRiders] = useState<Rider[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [nameError, setNameError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [activeDeliveries, setActiveDeliveries] = useState<Record<string, number>>({});

  const refresh = useCallback(() => {
    const list = getRiders();
    setRiders(list);
    // Count active deliveries per rider
    const orders = getAllOrders();
    const counts: Record<string, number> = {};
    orders
      .filter((o) => o.status === 'on_way' && o.rider)
      .forEach((o) => {
        counts[o.rider!] = (counts[o.rider!] ?? 0) + 1;
      });
    setActiveDeliveries(counts);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAdd = () => {
    if (!newName.trim()) {
      setNameError('Name is required.');
      return;
    }
    addRider(newName, newPhone);
    toast.success('Rider added!', `${newName.trim()} joined the team.`);
    setNewName('');
    setNewPhone('');
    setNameError('');
    setShowForm(false);
    refresh();
  };

  const handleToggle = (id: string, name: string, current: boolean) => {
    toggleRiderActive(id);
    refresh();
    toast.info(
      current ? 'Rider set to Off Duty' : 'Rider set to On Duty',
      `${name} is now ${current ? 'unavailable' : 'available'} for deliveries.`
    );
  };

  const handleDelete = (rider: Rider) => {
    if (confirmDelete === rider.id) {
      removeRider(rider.id);
      setConfirmDelete(null);
      refresh();
      toast.warning('Rider removed', `${rider.name} has been removed from the team.`);
    } else {
      setConfirmDelete(rider.id);
    }
  };

  const activeCount = riders.filter((r) => r.active).length;
  const inactiveCount = riders.length - activeCount;

  return (
    <AdminLayout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Delivery Riders</h1>
            <p className="text-sm text-stone-500 mt-0.5">Manage your delivery team</p>
          </div>
          <button
            onClick={() => { setShowForm((v) => !v); setNameError(''); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition cursor-pointer whitespace-nowrap ${
              showForm
                ? 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                : 'bg-orange-500 hover:bg-orange-600 text-white'
            }`}
          >
            <i className={showForm ? 'ri-close-line' : 'ri-user-add-line'} />
            {showForm ? 'Cancel' : 'Add Rider'}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-stone-200 px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
              <i className="ri-group-line text-orange-500 text-lg" />
            </div>
            <div>
              <p className="text-xs text-stone-400 font-medium">Total Riders</p>
              <p className="text-2xl font-bold text-stone-900">{riders.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
              <i className="ri-motorbike-line text-green-500 text-lg" />
            </div>
            <div>
              <p className="text-xs text-stone-400 font-medium">On Duty</p>
              <p className="text-2xl font-bold text-stone-900">{activeCount}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center shrink-0">
              <i className="ri-moon-line text-stone-400 text-lg" />
            </div>
            <div>
              <p className="text-xs text-stone-400 font-medium">Off Duty</p>
              <p className="text-2xl font-bold text-stone-900">{inactiveCount}</p>
            </div>
          </div>
        </div>

        {/* Add rider form */}
        {showForm && (
          <div className="bg-white border border-orange-200 rounded-xl p-5">
            <p className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
              <i className="ri-user-add-line text-orange-500" />
              New Rider
            </p>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5">
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => { setNewName(e.target.value); setNameError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder="e.g. Jordan Brown"
                  className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition ${
                    nameError
                      ? 'border-red-300 focus:ring-red-100 bg-red-50'
                      : 'border-stone-200 focus:ring-orange-100 focus:border-orange-300'
                  }`}
                />
                {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5">
                  Phone <span className="text-stone-300 font-normal normal-case">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder="+592-XXX-XXXX"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-300 transition"
                />
              </div>
              <button
                onClick={handleAdd}
                className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-lg transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line mr-1.5" />
                Add to Team
              </button>
            </div>
          </div>
        )}

        {/* Riders list */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {riders.length === 0 ? (
            <div className="py-16 flex flex-col items-center gap-3">
              <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center">
                <i className="ri-motorbike-line text-stone-400 text-xl" />
              </div>
              <p className="text-stone-400 text-sm">No riders yet — add one above</p>
            </div>
          ) : (
            <>
              <div className="bg-stone-50 border-b border-stone-200 grid grid-cols-[1fr_160px_120px_130px_100px] px-5 py-2.5 gap-4">
                {['Rider', 'Phone', 'Status', 'Deliveries', 'Actions'].map((h) => (
                  <span key={h} className="text-xs font-semibold text-stone-400 uppercase tracking-wide">
                    {h}
                  </span>
                ))}
              </div>

              {riders.map((rider, idx) => {
                const isDeleting = confirmDelete === rider.id;
                const deliveries = activeDeliveries[rider.name] ?? 0;

                return (
                  <div
                    key={rider.id}
                    className={`grid grid-cols-[1fr_160px_120px_130px_100px] items-center px-5 py-4 gap-4 transition ${
                      idx < riders.length - 1 ? 'border-b border-stone-100' : ''
                    } ${isDeleting ? 'bg-red-50' : 'hover:bg-stone-50/60'}`}
                  >
                    {/* Avatar + name */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarColor(rider.name)} flex items-center justify-center shrink-0`}>
                        <span className="text-white text-sm font-bold">
                          {rider.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-stone-800 truncate">{rider.name}</p>
                        {deliveries > 0 && (
                          <p className="text-xs text-orange-500 font-medium">
                            {deliveries} active delivery
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Phone */}
                    <p className="text-sm text-stone-500 truncate">
                      {rider.phone || <span className="text-stone-300 italic">—</span>}
                    </p>

                    {/* Status badge */}
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold w-fit ${
                      rider.active
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-stone-100 text-stone-400 border border-stone-200'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${rider.active ? 'bg-green-500' : 'bg-stone-300'}`} />
                      {rider.active ? 'On Duty' : 'Off Duty'}
                    </span>

                    {/* Toggle */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggle(rider.id, rider.name, rider.active)}
                        className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
                          rider.active ? 'bg-green-500' : 'bg-stone-300'
                        }`}
                        title={rider.active ? 'Set Off Duty' : 'Set On Duty'}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                            rider.active ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="text-xs text-stone-400">
                        {rider.active ? 'On' : 'Off'}
                      </span>
                    </div>

                    {/* Delete */}
                    <div className="flex items-center gap-1.5">
                      {isDeleting ? (
                        <>
                          <button
                            onClick={() => handleDelete(rider)}
                            className="px-2.5 py-1.5 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg cursor-pointer transition whitespace-nowrap"
                          >
                            Yes, remove
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="px-2 py-1.5 text-xs text-stone-500 hover:text-stone-700 rounded-lg cursor-pointer transition"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleDelete(rider)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-stone-300 hover:text-red-400 transition cursor-pointer"
                          title="Remove rider"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
